import React, { useMemo } from 'react';
import classnames from 'classnames';
import {
  Box,
  Typography,
  SidebarIcon,
  navigationItems,
  APP_ROUTE_PREFIXES,
  useCrossAppNavigate,
} from '@lucid/lego';
import styles from './QuickLinks.module.scss';
import { isNavigationEnabled } from '../../../config/featureFlags';
import { useLocaleNavigate } from '../../../hooks/useLocaleNavigate';
import { useStore } from '../../../store/store';
import { useEntitlements } from '../../../hooks/useEntitlements';

type QuickLinksProps = {
  theme: 'light' | 'dark';
  labels?: Record<string, string>;
};

const QuickLinks: React.FC<QuickLinksProps> = ({ theme, labels }) => {
  const navigate = useLocaleNavigate();
  const locale = useStore((state) => state.locale);
  const { entitlements, isLoading: isIdentityLoading } = useEntitlements();
  const activeEntitlements = isIdentityLoading ? undefined : entitlements;
  const workspaces = useMemo(
    () =>
      navigationItems
        .filter((workspace) => isNavigationEnabled(workspace.id, activeEntitlements))
        .map((workspace) => ({
          ...workspace,
          childrens: workspace.childrens?.filter((child) =>
            isNavigationEnabled(child.id, activeEntitlements)
          ),
        })),
    [activeEntitlements]
  );

  const handleNavigate = useCrossAppNavigate(APP_ROUTE_PREFIXES.partnerPortal, locale, navigate);

  return (
    <div className={styles.tocWrapper} data-theme={theme}>
      <Box pb={16}>
        <Typography variant="headline" size="small" color="primary-1" theme={theme}>
          Quick Links
        </Typography>
      </Box>
      <div className={styles.workspacesGrid}>
        {workspaces.map((workspace, index) => (
          <div
            key={workspace.id}
            className={classnames(
              styles.workspaceColumn,
              index % 2 === 0 ? styles.borderPrimary : styles.borderWhite
            )}
          >
            <div className={styles.workspaceHeader}>
              {workspace.icon && (
                <SidebarIcon name={workspace.icon.name} size={20} color="#dbd4c5" />
              )}
              <Typography
                variant="body"
                size="large"
                color="primary-2"
                theme={theme}
                className={styles.workspaceTitle}
              >
                {labels?.[`sidebar.${workspace.id}`] ?? workspace.label}
              </Typography>
            </div>
            <ul className={styles.childrenList}>
              {workspace.childrens?.map((child) => (
                <li key={child.id}>
                  <a
                    href={child.path}
                    className={styles.childLink}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigate(child.path);
                    }}
                  >
                    {child.icon && (
                      <SidebarIcon
                        name={child.icon.name}
                        size={16}
                        color={theme === 'dark' ? '#9CA3AF' : '#6B7280'}
                      />
                    )}
                    <Typography variant="body" size="medium" color="white" theme={theme}>
                      {labels?.[`sidebar.${workspace.id}.${child.id}`] ?? child.label}
                    </Typography>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuickLinks;
